import type { FC } from "react";
import { MultiVariateFeature } from "@viagogo/feature";
import type { CategoryEventListViewModel } from "src/http/types";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useAssetsUrl } from "src/shared/hooks/useAssetsUrl";
import { CarouselId } from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import { PROMO_BANNER_CITY_ID } from "src/website-apps/shared/components/promo-banner/PromoBanner";
import {
  TheaterPromoBanner,
  type TheaterPromoBannerMotion,
  type TheaterPromoBannerShow,
} from "src/website-apps/shared/components/theater-promo-banner/TheaterPromoBanner";

// Grouping id the CTA links to; also the key each experiment arm maps onto.
const BOM_CATEGORY_ID = 495_745;
// Leaf performer category the show's events are fetched from.
const BOM_PERFORMER_CATEGORY_ID = 150_057_581;

// Pages the banner renders on. Must stay in sync with
// CategoryControllerService.PromoBannerTheaterCategoryIds.
export const THEATER_PROMO_BANNER_CATEGORY_IDS: readonly number[] = [12_585, 1];

// Arm index (1-based, matching MultiVariateFeature's variants order) → the ids the show's
// carousel card may carry. The leaf performer category is first: it is the real category id,
// and the one the server pins by; the grouping id is the URL segment the CTA links to.
const BOM_CATEGORY_IDS = [BOM_PERFORMER_CATEGORY_ID, BOM_CATEGORY_ID] as const;

export const featuredCategoryIdsForVariant = (variant: number): readonly number[] | undefined => {
  if (variant === 1 || variant === 2) return BOM_CATEGORY_IDS;
  return undefined;
};

// CRH-762 header assets are uploaded alongside the 2a promo assets.
const ASSETS_PATH = "img/open-distribution-banners/Experiment 2";

interface ShowConfig {
  name: string;
  url: string;
}

const BOM_SHOW: ShowConfig = {
  name: "The Book of Mormon",
  // Carries the same New York override as the 2a banner CTA, so the grid lands on the Broadway run
  // rather than the visitor's location.
  url: `/the-book-of-mormon-tickets/grouping/495745?bannerCityId=${PROMO_BANNER_CITY_ID}`,
};

export interface FeaturedShowModuleConfig {
  name: string;
  moduleKicker: string;
  // The show's landing URL, carrying the same New York override as the banner CTA.
  url: string;
  // The show's entity id, reported as the CTA CarouselInteraction `itemId`.
  entityId: number;
  // The banner's CarouselInteraction id. Per-arm so clicks and in-views are attributable without
  // joining back to participation.
  carouselId: CarouselId;
}

// The two arms report under their own CarouselInteraction id. Numbering matches
// MultiVariateFeature's variants order below.
export const theaterPromoBannerCarouselIdForVariant = (variant: number): CarouselId =>
  variant === 2 ? CarouselId.TheaterPromoBannerVariant2 : CarouselId.TheaterPromoBannerVariant1;

// The featured-show event module (req 3) renders in the page body for the arm that carries events
// (2). The arm mapping itself is owned by requirement 9 — do not fold these arms together here.
// v1 carries no body module: its CTA is the liquid-glass pill in the hero. The kicker is resolved
// from CMS by the caller and passed in.
export const featuredShowModuleForVariant = (
  variant: number,
  moduleKicker: string,
): FeaturedShowModuleConfig | undefined => {
  if (variant === 2) {
    return {
      name: BOM_SHOW.name,
      moduleKicker,
      url: BOM_SHOW.url,
      entityId: BOM_CATEGORY_ID,
      carouselId: theaterPromoBannerCarouselIdForVariant(variant),
    };
  }
  return undefined;
};

// The hero is a still raster fetched from the CDN; its 2.5s scale push is a CSS keyframe
// (see theaterPromoBanner `heroImageAnimated`), so no animation payload ships in the bundle.
const toMotion = (config: ShowConfig, assetsUrl: string, fileName: string): TheaterPromoBannerMotion => ({
  type: "image",
  src: `${assetsUrl}/${ASSETS_PATH}/${fileName}`,
  ariaLabel: config.name,
});

interface BannerProps {
  config: ShowConfig;
  categoryTitle: string;
  formattedFromPrice: string | undefined;
  categoryId: number;
  carouselId: CarouselId;
  // v1 only: render the phone CTA as the liquid-glass pill over the hero instead of the desktop card.
  headerCta: boolean;
}

const FeaturedShowBanner: FC<BannerProps> = ({
  config,
  categoryTitle,
  formattedFromPrice,
  categoryId,
  carouselId,
  headerCta,
}) => {
  const assetsUrl = useAssetsUrl();

  const show: TheaterPromoBannerShow = {
    name: config.name,
    url: config.url,
    motion: toMotion(config, assetsUrl, "bom_header.jpg"),
    mobileMotion: toMotion(config, assetsUrl, "bom_header_tall.jpg"),
  };
  if (formattedFromPrice) {
    show.formattedFromPrice = formattedFromPrice;
  }

  return (
    <TheaterPromoBanner
      categoryTitle={categoryTitle}
      show={show}
      entityId={BOM_CATEGORY_ID}
      categoryId={categoryId}
      carouselId={carouselId}
      headerCta={headerCta}
    />
  );
};

interface Props {
  model: CategoryEventListViewModel;
}

export const TheaterPromoBannerFeature: FC<Props> = ({ model }) => {
  const categoryTitle = model.categorySummary?.categoryName ?? "";

  const renderShow = (config: ShowConfig, variant: number) => () => (
    <FeaturedShowBanner
      config={config}
      categoryTitle={categoryTitle}
      formattedFromPrice={model.featuredShowFromPrice ?? undefined}
      categoryId={model.categoryId}
      carouselId={theaterPromoBannerCarouselIdForVariant(variant)}
      headerCta={variant === 1}
    />
  );

  return (
    <MultiVariateFeature
      id={FeatureSetEnum.TPromoBannerATG2b}
      control={() => null}
      variants={[renderShow(BOM_SHOW, 1), renderShow(BOM_SHOW, 2)]}
    />
  );
};
